// src/plugins/edithcustomwidget.js
import { Plugin } from "@ckeditor/ckeditor5-core";
import { toWidget, toWidgetEditable, Widget } from "@ckeditor/ckeditor5-widget";
import InsertEdithCustomWidgetCommand from "./command.js";

export default class EdithCustomWidget extends Plugin {
    static get requires() {
        return [Widget];
    }

    static get pluginName() {
        return "EdithCustomWidget";
    }

    /**
     * Crée le handle de sélection (writer fourni par CKEditor) pour la poignée de sélection du widget.
     * Cette poignée permet de sélectionner le widget pour le modifier ou le supprimer.
     */
    _createSelectionHandle(editor, writer) {
        return writer.createUIElement(
            "div",
            {
                class: "ck ck-widget__selection-handle",
                tabindex: "-1",
            },
            function (domDocument) {
                const domElement = this.toDomElement(domDocument);
                domElement.innerHTML = `
<svg class="ck ck-icon ck-reset_all-excluded ck-icon_inherit-color" viewBox="0 0 16 16">
    <path d="M4 0v1H1v3H0V.5A.5.5 0 0 1 .5 0H4zm8 0h3.5a.5.5 0 0 1 .5.5V4h-1V1h-3V0zM4 16H.5a.5.5 0 0 1-.5-.5V12h1v3h3v1zm8 0v-1h3v-3h1v3.5a.5.5 0 0 1-.5.5H12z"></path>
    <path fill-opacity=".256" d="M1 1h14v14H1z"></path>
    <g class="ck-icon__selected-indicator">
        <path d="M7 0h2v1H7V0zM0 7h1v2H0V7zm15 0h1v2h-1V7zm-8 8h2v1H7v-1z"></path>
        <path fill-opacity=".254" d="M1 1h14v14H1z"></path>
    </g>
</svg>
                `;
                domElement.onclick = (evt) => {
                    evt.stopPropagation();
                    // Trouve le widget parent DOM
                    let parent = domElement.parentElement;
                    while (parent && !parent.classList.contains("edith-widget")) {
                        parent = parent.parentElement;
                    }
                    if (!parent) return;
                    // Associe DOM et modèle (gestion multi-widget)
                    const root = editor.model.document.getRoot();
                    let i = 0;
                    for (const el of root.getChildren()) {
                        if (el.name === "edithCustomWidget") {
                            if (i === Array.from(document.querySelectorAll(".edith-widget")).indexOf(parent)) {
                                editor.model.change((writer) => {
                                    writer.setSelection(el, "on");
                                });
                                break;
                            }
                            i++;
                        }
                    }
                };
                return domElement;
            },
        );
    }

    init() {
        const editor = this.editor;

        // Schéma
        editor.model.schema.register("edithCustomWidget", {
            isObject: true,
            allowWhere: "$block",
        });

        editor.model.schema.register("edithCustomContent", {
            isLimit: true,
            allowIn: "edithCustomWidget",
            allowContentOf: "$root",
        });

        // Conversion modèle → vue (édition)
        editor.conversion.for("editingDowncast").elementToElement({
            model: "edithCustomWidget",
            view: (modelElement, { writer }) => {
                const div = writer.createContainerElement("div", {
                    class: "edith-widget ck-widget ck-widget_with-selection-handle",
                });

                // Poignée en premier enfant (writer passé ici)
                writer.insert(writer.createPositionAt(div, 0), this._createSelectionHandle(editor, writer));

                return toWidget(div, writer, { label: "Bloc personnalisé" });
            },
        });

        editor.conversion.for("editingDowncast").elementToElement({
            model: "edithCustomContent",
            view: (modelElement, { writer }) => {
                const editable = writer.createEditableElement("div", {
                    class: "edith-widget__content",
                });
                return toWidgetEditable(editable, writer, {
                    label: "Contenu éditable du bloc",
                });
            },
        });

        // Conversion modèle → vue (export HTML)
        editor.conversion.for("dataDowncast").elementToElement({
            model: "edithCustomWidget",
            view: (modelElement, { writer }) =>
                writer.createContainerElement("div", {
                    class: "edith-widget",
                }),
        });

        editor.conversion.for("dataDowncast").elementToElement({
            model: "edithCustomContent",
            view: (modelElement, { writer }) =>
                writer.createContainerElement("div", {
                    class: "edith-widget__content",
                }),
        });

        // Conversion data → modèle
        editor.conversion.for("upcast").elementToElement({
            view: {
                name: "div",
                classes: "edith-widget",
            },
            model: "edithCustomWidget",
        });

        editor.conversion.for("upcast").elementToElement({
            view: {
                name: "div",
                classes: "edith-widget__content",
            },
            model: "edithCustomContent",
        });

        // Commande d'insertion du widget
        editor.commands.add("insertEdithCustomWidget", new InsertEdithCustomWidgetCommand(editor));
    }
}
